/**
 * `/settings/audit-export` — the managing-partner-only firm-wide audit surface
 * (routing-and-state.md §1, under `_partner-only`; T-6.14 fills the Sprint-1
 * placeholder). Shows the last daily integrity-check verdict (T-6.15 —
 * `GET /audit/integrity`) and the firm-wide NDJSON export (audit.export;
 * audited server-side as `audit_export_requested`). The two-guard wall stands:
 * `firm_admin` reaches `_settings` but NOT this route (§2.1 case-data wall —
 * the firm-wide log carries case-linked content).
 *
 * Built on the design-system primitives (UI/UX Flow 25).
 */
import { Download } from '../../components/ui/icons.ts';
import type { JSX } from 'react';

import {
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Notice,
  PageHeader,
  SkeletonText,
  buttonClassName,
} from '../../components/ui/index.ts';
import { useAuditIntegrity } from '../../hooks/use-audit.ts';
import { api, getErrorMessage, type AuditIntegrityResponse } from '../../lib/api.ts';
import { getRequestId } from '../../lib/request-id.ts';
import { formatDate } from '../../lib/format/date.ts';

import '../../styles/audit.css';

function checkedAt(verdict: AuditIntegrityResponse): string {
  return verdict.checked_at
    ? formatDate(verdict.checked_at, { withTime: true })
    : 'an unknown time';
}

function IntegrityVerdict({ verdict }: { verdict: AuditIntegrityResponse }): JSX.Element {
  if (verdict.status === 'never_run') {
    return (
      <Notice role="status">
        The daily integrity check has not run yet. It runs at 03:00 UTC every day.
      </Notice>
    );
  }
  if (verdict.ok) {
    return (
      <Notice tone="success">
        <span className="argus-audit-verdict">
          {`PASSED — ${String(verdict.events_verified)} events re-hashed clean, checked ${checkedAt(verdict)}`}
        </span>
      </Notice>
    );
  }
  return (
    <Notice tone="warning">
      <span className="argus-audit-verdict">
        {`FAILED at sequence ${String(verdict.first_tamper_sequence)}, checked ${checkedAt(verdict)} — incident response is running`}
      </span>
    </Notice>
  );
}

export function AuditExportScreen(): JSX.Element {
  const integrity = useAuditIntegrity();

  return (
    <div className="argus-page">
      <PageHeader
        title="Audit export"
        description="Export the firm's complete tamper-evident audit log. Every export is itself recorded on the chain."
      />

      <Card>
        <CardHeader
          title="Daily integrity check"
          description="The whole chain is re-hashed every day at 03:00 UTC; this is the last verdict."
        />
        <CardBody>
          {integrity.isPending ? (
            <SkeletonText lines={2} label="Loading the last verdict" />
          ) : integrity.isError ? (
            <ErrorState
              title="Could not load the integrity status."
              message={getErrorMessage(integrity.error)}
              requestId={getRequestId(integrity.error)}
              onRetry={() => void integrity.refetch()}
              retrying={integrity.isFetching}
            />
          ) : (
            <IntegrityVerdict verdict={integrity.data} />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Firm-wide export (NDJSON)"
          description="One JSON object per audit event, carrying every hash-chain field. A third party can re-compute the whole chain from this file with the published verifier. Managing partner only."
        />
        <CardBody>
          <div className="stack">
            <div className="row">
              <a
                href={api.audit.firmExportUrl()}
                className={buttonClassName({ variant: 'primary' })}
              >
                <Download size={16} aria-hidden="true" />
                Download audit log (.ndjson)
              </a>
            </div>
            <p className="text-muted text-xs">
              Verification bundles per case (CSV, certificate and the embedded Python verifier) are
              exported from each case's Audit trail page. On-chain anchoring is pending V1 GA; the
              in-database chain is fully verifiable today.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
